import { all, put, takeLatest } from 'redux-saga/effects';
import { CondOperator, RequestQueryBuilder } from '@nestjsx/crud-request';
import {
  CANCEL_EVENT,
  CREATE_EVENT,
  DISABLE_EVENT,
  GET_DIGITAL_MARKETING,
  GET_EVENTS,
  GET_EVENT_BY_ID,
  GET_FILTERED_EVENTS,
  GET_PAST_EVENTS,
  SEND_NOTIFICATION_SMS_TO_VENDORS,
  UPDATE_EVENT,
} from './constants';
import Api from '../../commons/api';
import * as EventActions from './action';
import * as VenueActions from '../venues/action';
import appConfig from '../../config/appConfig';

import { GROUP_USER } from '../../config/appGroupConfig';
import { APP_ROUTES } from '../../constants/app-routes';
import { onDisplayAlert } from '../../utils/alerts/alerts';

const controller = 'events';
const controllerv = 'venues/venues';
const venueController = 'venues/venues';
const createBaseQueryBuilder = () =>
  RequestQueryBuilder.create()
    .setJoin({ field: 'parameters_event' })
    .setJoin({ field: 'equipments_event' });

function* fetchGetEventsByVenue({ payload: { venueId } }) {
  try {
    const qb = RequestQueryBuilder.create()
      .setFilter({
        field: 'fk_venue',
        operator: CondOperator.EQUALS,
        value: venueId,
      })
      .setJoin({ field: 'parameters_event' })
      .setJoin({ field: 'equipments_event' })
      .setJoin({ field: 'surcharges' })
      .setJoin({ field: 'space_dimensions' })
      .setJoin({
        field: 'vendorEvents',
        select: ['fk_vendor', 'fk_event'],
      });

    const { data } = yield Api.get(`${controller}?${qb.query()}`);
    yield put(EventActions.getEventsByVenueSuccess(data));
  } catch (err) {
    yield put(EventActions.getEventsByVenueFailed(err));
  }
}

function buildRequestQueryByRole(userId, userRole) {
  const query = createBaseQueryBuilder()
    .setJoin({ field: 'venue' })
    .setJoin({ field: 'venue.user_owner' })
    .setJoin({ field: 'vendorEvents' })
    .setJoin({ field: 'vendorEvents.vendor' });
  switch (userRole) {
    case GROUP_USER.VENDOR:
      query.setFilter({
        field: 'vendorEvents.fk_vendor',
        operator: CondOperator.EQUALS,
        value: userId,
      });
      break;
    case GROUP_USER.VENUE:
      query.setFilter({
        field: 'venue.user_owner.id',
        operator: CondOperator.EQUALS,
        value: userId,
      });
      break;
    default:
      break;
  }
  return query.query();
}

function* fetchGetPastEvents({ payload }) {
  const now = new Date();
  const hasVendors = (event) => event.vendorEvents?.length > 0;
  try {
    const { userId, userRole } = payload;

    const query = buildRequestQueryByRole(userId, userRole);
    const { data } = yield Api.get(`${controller}?${query}`);

    const pastEvents = data
      .filter((event) => hasVendors(event))
      .sort((a, b) => {
        const dateA = new Date(a.parameters_event.event_date).getTime();
        const dateB = new Date(b.parameters_event.event_date).getTime();
        return dateB - dateA;
      });

    console.log('Past events:', pastEvents);
    yield put(EventActions.getPastEventsSuccess(pastEvents));
  } catch (err) {
    yield put(EventActions.getPastEventsFailed(err));
  }
}

function* fetchGetFilteredEvents({ payload }) {
  try {
    // Make a shallow copy of the object, and filter valid values
    const filter = { ...payload.filter };
    Object.keys(filter).forEach((key) => {
      const value = filter[key];
      if (value === '' || value === undefined) delete filter[key];
    });

    const { data } = yield Api.post(`${controller}/${controller}/filter`, filter);
    payload.onDataFetched(data);

    yield put(EventActions.getFilteredEventsSuccess(data));
  } catch (err) {
    yield put(EventActions.getFilteredEventsFailed(err));
  }
}

function* fetchGetEventById({ payload }) {
  try {
    let fieldsToSelect = ['contact_name'];
    if (payload.role) {
      fieldsToSelect =
        payload.role === (GROUP_USER.OWNER_VENUE || GROUP_USER.VENUE)
          ? ['contact_name', 'contact_email', 'contact_phone_number', 'name', 'fk_user']
          : ['contact_name', 'name', 'fk_user'];
      // console.log('fieldsToSelect ', fieldsToSelect);
    }
    const query = createBaseQueryBuilder()
      .setJoin({ field: 'venue', select: fieldsToSelect })
      .setJoin({ field: 'vendorEvents' })
      .setJoin({ field: 'surcharges' })
      .setJoin({ field: 'vendorEvents.vendor_event_contact' })
      .setJoin({ field: 'vendorEvents.vendor' })
      .setJoin({ field: 'space_dimensions' })
      .setJoin({ field: 'state' })
      .setJoin({ field: 'city' })
      .query();
    const { data } = yield Api.get(`${controller}/events/${payload.eventId}?${query}`);
    if (data?.venue?.id) {
      yield put(VenueActions.getVenueById(data.venue.id));
    }
    yield put(EventActions.getEventSuccess(data));
  } catch (err) {
    yield put(EventActions.getEventByIdFailed(err));
  }
}

function* fetchCreateEvent({ payload }) {
  const { event } = payload;
  try {
    const { data } = yield Api.post(`${controller}/events`, event);
    console.log('🍉 event request ', event);
    console.log('🍉 event response ', data);
    if (event.marketingData) {
      yield Api.post(`${venueController}/multimedia?venue_id=${data.id}`, event.marketingData);
    }
    yield put(EventActions.createEventSuccess(data));
    onDisplayAlert({
      title: data.name,
      description: 'The event was created successfully',
      type: 'success',
      preConfirm: () => window.location.reload(),
    });
  } catch (err) {
    console.log({ err });
    onDisplayAlert({
      title: event.event.name,
      description: 'A problem occurred creating an event',
      type: 'error',
    });
    yield put(EventActions.createEventFailed(err));
  }
}

function* fetchSendNotificationSMSToVendors({ payload }) {
  const { eventId, message } = payload;
  try {
    const { data } = yield Api.post(`${controller}/${controller}/sms/${eventId}`, { message });
    yield put(EventActions.sendNotificationSMSToVendorsSuccess(data));
    onDisplayAlert({
      title: 'Notifications SMS',
      description: 'This task was sent successfully',
      type: 'success',
    });
  } catch (err) {
    onDisplayAlert({
      title: 'Notification SMS Error',
      description: 'Error sending notification SMS to vendors',
      type: 'error',
    });
    yield put(EventActions.sendNotificationSMSToVendorsFailed(err));
  }
}
const generatePictures = (pictures) =>
  pictures.map((i) => ({
    type: 'venue',
    key: i.key,
    name: i.fileName,
  }));

function* fetchUpdateEvent({ payload }) {
  const { event, navigate } = payload;
  const { photo, venueId } = event;
  try {
    /**
     * Flag
     * true:general event update
     * false: Event fees update
     */
    const noEditFees = !!event?.event_parameter?.max_applications;

    // Validate max_application number, Get current applications value
    const qb = RequestQueryBuilder.create()
      .setFilter({
        field: 'id',
        operator: CondOperator.EQUALS,
        value: event.id,
      })
      .setJoin({ field: 'parameters_event' })
      .setJoin({ field: 'equipments_event' })
      .setJoin({ field: 'surcharges' })
      .setJoin({ field: 'space_dimensions' })
      .setJoin({
        field: 'vendorEvents',
        select: ['fk_vendor', 'fk_event'],
      });

    const resp = yield Api.get(`${controller}?${qb.query()}`);
    const data = resp.data[0];
    const updatedEvent = { ...event };
    const currentApplications = data.vendorEvents.length;

    if (photo?.length > 0) {
      const multimediaList = generatePictures(photo);
      yield Api.post(`${controllerv}/multimedia?venue_id=${venueId}`, multimediaList);
      updatedEvent.photo = photo[0].key;
    } else {
      delete updatedEvent.photo;
    }
    if (currentApplications <= updatedEvent?.event_parameter?.max_applications || !noEditFees) {
      // Reject edit operation
      yield Api.post(`${controller}/events/updateEvents/update/${updatedEvent.id}`, updatedEvent);
      yield put(EventActions.updateEventSuccess(updatedEvent));
      if (navigate) navigate(APP_ROUTES.DASHBOARD);
    } else {
      // Update location
      const updatePage = APP_ROUTES.UPDATE_EVENT.replace(':id', updatedEvent.id);

      if (navigate)
        navigate(updatePage, {
          state: {
            event: data,
          },
        });

      yield put(
        EventActions.updateEventFailed(`While editing some new application(s) were submitted, the 
      min value for Max applications is ${currentApplications}.`),
      );
    }
  } catch (err) {
    console.log('🍉 ~ file: saga.js: function*fetchUpdateEvent ~ err:', err);
    yield put(EventActions.updateEventFailed('Sorry, event update failed!'));
  }
}

function* fetchCancelEvent({ payload }) {
  const { eventId } = payload;
  try {
    yield Api.put(`${controller}/events/${eventId}`, { status: false });
    yield put(EventActions.onCancelEventSuccess(eventId));
  } catch (err) {
    yield put(EventActions.updateEventFailed(err));
  }
}

function* fetchDisableEvent({ payload }) {
  try {
    const { eventId } = payload;
    const eventUrl = APP_ROUTES.EVENT_DETAILS.replace(':eventId', eventId);
    yield Api.post(`${controller}/${controller}/disable`, {
      eventId,
      eventUrl,
    });
    yield put(EventActions.onCancelEventSuccess(eventId));
  } catch (err) {
    yield put(EventActions.updateEventFailed(err));
  }
}

function* fetchGetDigitalMarketing({ payload }) {
  try {
    let baseURL = `${window.location.protocol}//${window.location.hostname}`;
    let url;
    const { eventId } = payload;
    const { data } = yield Api.get(`${controller}/${controller}/digital-content/${eventId}`);
    if (process.env.REACT_APP_STAGE === 'local') {
      baseURL = 'https://app.dev.fayven.com';
    }
    if (data) url = `${baseURL}/public/${data.key}`;

    yield put(EventActions.getDigitalMarketingSuccess(url));
  } catch (err) {
    yield put(EventActions.getDigitalMarketingFailed(err));
  }
}

function* ActionWatcher() {
  yield takeLatest(CREATE_EVENT, fetchCreateEvent);
  yield takeLatest(GET_EVENTS, fetchGetEventsByVenue);
  yield takeLatest(GET_PAST_EVENTS, fetchGetPastEvents);
  yield takeLatest(GET_EVENT_BY_ID, fetchGetEventById);
  yield takeLatest(GET_FILTERED_EVENTS, fetchGetFilteredEvents);
  yield takeLatest(CANCEL_EVENT, fetchCancelEvent);
  yield takeLatest(DISABLE_EVENT, fetchDisableEvent);
  yield takeLatest(UPDATE_EVENT, fetchUpdateEvent);
  yield takeLatest(SEND_NOTIFICATION_SMS_TO_VENDORS, fetchSendNotificationSMSToVendors);
  yield takeLatest(GET_DIGITAL_MARKETING, fetchGetDigitalMarketing);
}

export default function* EventSaga() {
  yield all([ActionWatcher()]);
}
